import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceArea, ReferenceLine,
} from 'recharts';
import { getGPS, getOpponents, getDrills } from '../../api';
import { useUrlParam, useUrlParamArray } from '../../hooks/useUrlParam';
import { useSeason, dateToSeason } from '../../SeasonContext';

// ── Period order — derived per match, not hardcoded ────────────────────────────
// Matches run 90' standard, 120' for a 4x30 preseason game, 150' for 2x75, etc.,
// so the period list (Match-0-5, Match-5-10, ...) is read off whatever segments
// actually exist in that match's drills instead of assuming exactly 18 (90').
function derivePeriodOrder(drills) {
  const titles = new Set();
  drills.forEach(d => { if (/^Match-\d+-\d+$/.test(d.drill_title || '')) titles.add(d.drill_title); });
  return [...titles].sort((a, b) => Number(a.match(/^Match-(\d+)-/)[1]) - Number(b.match(/^Match-(\d+)-/)[1]));
}
const periodEndMin   = p => Number(p.match(/-(\d+)$/)?.[1] ?? 0);
const periodStartMin = p => Number(p.match(/^Match-(\d+)-/)?.[1] ?? 0);

// Exported so the Comparison Builder reuses the exact same metric set instead
// of hardcoding a separate list.
export const METRICS = [
  { key: 'hmld', label: 'HMLD' },
  { key: 'hid',  label: 'HID' },
  { key: 'td',   label: 'Total Dist.' },
  { key: 'sd',   label: 'Sprint Dist.' },
  { key: 'sa',   label: 'Short Actions' },
  { key: 'expl', label: 'Explosive Dist.' },
];

// Exported so the Comparison Builder reuses the same per-player color palette.
export const PLAYER_COLORS = [
  '#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6',
  '#ec4899','#06b6d4','#84cc16','#f97316','#6366f1',
  '#14b8a6','#f43f5e','#a855f7','#22c55e','#d946ef',
];

const fmt0   = n => (n == null || isNaN(n) ? '—' : Math.round(n).toLocaleString());
const dropColor = pct => pct > 0 ? '#34d399' : pct >= -10 ? '#fb923c' : '#f87171';
const fmtDate = d => { if (!d) return ''; const [, m, day] = d.split('-'); return `${day}/${m}`; };

// ── Heatmap cell color ────────────────────────────────────────────────────────
function cellBg(value, rowValues) {
  if (value == null || value === 0) return 'transparent';
  const valid = rowValues.filter(v => v != null && v > 0);
  if (!valid.length) return 'transparent';
  const avg = valid.reduce((s, v) => s + v, 0) / valid.length;
  const max = Math.max(...valid);
  if (value >= avg) {
    const ratio = max > avg ? (value - avg) / (max - avg) : 0;
    return `rgba(16,185,129,${0.15 + ratio * 0.55})`;
  }
  const min = Math.min(...valid);
  const ratio = avg > min ? (avg - value) / (avg - min) : 0;
  return `rgba(239,68,68,${ratio * 0.3})`;
}
function cellTextColor(value, rowValues) {
  const valid = rowValues.filter(v => v != null && v > 0);
  if (!valid.length || value == null) return 'var(--text-muted)';
  const max = Math.max(...valid);
  const ratio = max > 0 ? value / max : 0;
  return ratio > 0.6 ? '#fff' : 'var(--text-primary)';
}

// ── Multi-select player dropdown ──────────────────────────────────────────────
function PlayerMultiSelect({ players, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const allSelected = selected.length === 0 || selected.length === players.length;
  const toggle = name => onChange(
    selected.includes(name) ? selected.filter(n => n !== name) : [...selected, name]
  );
  const label = allSelected ? 'All players'
    : selected.length === 1 ? selected[0]
    : `${selected.length} players`;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px 6px 12px',
        borderRadius: 8, border: `1px solid ${!allSelected ? '#3b82f6' : 'var(--border-strong)'}`,
        background: !allSelected ? 'rgba(59,130,246,0.1)' : 'var(--bg-card)',
        color: !allSelected ? '#93c5fd' : 'var(--text-primary)',
        fontSize: '0.83rem', fontFamily: 'inherit', cursor: 'pointer', outline: 'none', minWidth: 140,
      }}>
        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: '0.15s', flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 300,
          background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 10,
          minWidth: 200, maxHeight: 320, overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)', padding: '5px 0',
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', cursor: 'pointer', fontSize: '0.82rem', color: 'var(--text-primary)', background: allSelected ? 'rgba(59,130,246,0.07)' : 'transparent' }}>
            <input type="checkbox" checked={allSelected} onChange={() => onChange([])} style={{ accentColor: '#3b82f6', flexShrink: 0 }} />
            All players
          </label>
          <div style={{ height: 1, background: 'var(--border)', margin: '3px 0' }} />
          {players.map(name => (
            <label key={name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', cursor: 'pointer', fontSize: '0.82rem', color: 'var(--text-primary)', background: selected.includes(name) ? 'rgba(59,130,246,0.07)' : 'transparent' }}>
              <input type="checkbox" checked={selected.includes(name)} onChange={() => toggle(name)} style={{ accentColor: '#3b82f6', flexShrink: 0 }} />
              {name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Custom tooltip ─────────────────────────────────────────────────────────────
function PeriodTooltip({ active, payload, label, metricLabel }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 11, minWidth: 120 }}>
      <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 5 }}>{label} min</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)', marginBottom: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>{p.dataKey === '__avg' ? 'Team Avg' : p.dataKey}</span>
          <span style={{ fontWeight: 600, color: 'var(--text-primary)', marginLeft: 6 }}>{fmt0(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function InMatchAnalysis({ onNavigate }) {
  const { selectedSeasons } = useSeason();
  const [sessions,   setSessions]   = useState([]);
  const [opponents,  setOpponents]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [sessionId,  setSessionId]  = useUrlParam('session_id', '');
  const [drills,     setDrills]     = useState([]);
  const [drillsLoad, setDrillsLoad] = useState(false);
  const [selPlayers, setSelPlayers] = useUrlParamArray('players');   // [] = all
  const [metricKey,  setMetricKey]  = useState('hmld');

  // ── Fetch match sessions + opponents ──────────────────────────────────────────
  useEffect(() => {
    Promise.all([getGPS(), getOpponents()])
      .then(([gps, opps]) => {
        const matches = (gps || []).filter(s => s.session_type === 'match')
          .sort((a, b) => (b.session_date || '').localeCompare(a.session_date || ''));
        setSessions(matches);
        setOpponents(opps || []);
        if (matches.length) setSessionId(String(matches[0].id));
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredSessions = useMemo(
    () => sessions.filter(s => {
      const d = s.session_date?.slice(0, 10);
      if (!d) return true;
      const season = dateToSeason(d);
      return !season || selectedSeasons.length === 0 || selectedSeasons.includes(season);
    }),
    [sessions, selectedSeasons],
  );

  // When season filter changes and current session is no longer visible, pick the latest
  useEffect(() => {
    if (!filteredSessions.length) { setSessionId(''); return; }
    if (!filteredSessions.find(s => String(s.id) === sessionId)) {
      setSessionId(String(filteredSessions[0].id));
    }
  }, [filteredSessions]);

  // ── Fetch drills when session changes ─────────────────────────────────────────
  useEffect(() => {
    if (!sessionId) { setDrills([]); return; }
    setDrillsLoad(true);
    setSelPlayers([]);
    getDrills(sessionId)
      .then(d => setDrills(d || []))
      .catch(() => setDrills([]))
      .finally(() => setDrillsLoad(false));
  }, [sessionId]);

  // ── Period order for this match (18 periods for 90', 24 for 120', ...) ────────
  const periodOrder = useMemo(() => derivePeriodOrder(drills), [drills]);
  const periodLabels = useMemo(() => periodOrder.map(p => p.replace('Match-', '')), [periodOrder]);
  // Halves split the available periods down the middle rather than assuming 9+9.
  const halfCount = Math.ceil(periodOrder.length / 2);
  // Fatigue window: first 60 min vs the match's own final 30 min — same absolute
  // windows regardless of total duration, so a 120' match still compares an
  // early window against a late one rather than needing the whole match covered.
  const matchTotalMinutes = periodOrder.length ? periodEndMin(periodOrder[periodOrder.length - 1]) : 90;
  const first60Periods = useMemo(() => periodOrder.filter(p => periodEndMin(p) <= 60), [periodOrder]);
  const last30Periods  = useMemo(() => periodOrder.filter(p => periodStartMin(p) >= matchTotalMinutes - 30), [periodOrder, matchTotalMinutes]);
  const minMinutesForFatigue = Math.max(0, matchTotalMinutes - 15); // mirrors the original 90-15=75 threshold

  // ── Period drills only ────────────────────────────────────────────────────────
  const periodDrills = useMemo(() =>
    drills.filter(d => periodOrder.includes(d.drill_title)),
    [drills, periodOrder]
  );
  // Optional numeric suffix so non-standard formats split into more than 2
  // segments (e.g. "Match-First Half-Full-1"/"-2" for a 4x30 match) still match.
  const halfDrills = useMemo(() =>
    drills.filter(d => /^match-(first|second) half-full(-\d+)?$/.test((d.drill_title || '').trim().toLowerCase())),
    [drills]
  );

  // ── All player names from period drills ───────────────────────────────────────
  const allPlayers = useMemo(() => {
    const names = [...new Set(periodDrills.map(d => d.player_name).filter(Boolean))];
    return names.sort((a, b) => a.localeCompare(b));
  }, [periodDrills]);

  // ── Active players (selection or all) ────────────────────────────────────────
  const activePlayers = useMemo(() =>
    (selPlayers.length > 0 && selPlayers.length < allPlayers.length) ? selPlayers : allPlayers,
    [selPlayers, allPlayers]
  );
  const isAllMode = selPlayers.length === 0 || selPlayers.length === allPlayers.length;

  // ── Per-player, per-period lookup map ────────────────────────────────────────
  const byPlayerPeriod = useMemo(() => {
    const map = {};
    for (const d of periodDrills) {
      if (!d.player_name) continue;
      if (!map[d.player_name]) map[d.player_name] = {};
      map[d.player_name][d.drill_title] = d;
    }
    return map;
  }, [periodDrills]);

  // ── Chart data (line chart) ───────────────────────────────────────────────────
  const chartData = useMemo(() => {
    return periodOrder.map((period, idx) => {
      const entry = { period: periodLabels[idx] };
      if (isAllMode) {
        // Team average for this period
        const vals = allPlayers.map(n => Number(byPlayerPeriod[n]?.[period]?.[metricKey]) || null).filter(v => v != null);
        entry['__avg'] = vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
      } else {
        activePlayers.forEach(name => {
          const v = byPlayerPeriod[name]?.[period]?.[metricKey];
          entry[name] = v != null ? Number(v) : null;
        });
      }
      return entry;
    });
  }, [byPlayerPeriod, activePlayers, allPlayers, metricKey, isAllMode, periodOrder, periodLabels]);

  const chartLines = useMemo(() => {
    if (isAllMode) return [{ key: '__avg', label: 'Team Avg', color: '#3b82f6', bold: true }];
    return activePlayers.map((name, i) => ({
      key: name, label: name, color: PLAYER_COLORS[i % PLAYER_COLORS.length], bold: false,
    }));
  }, [activePlayers, isAllMode]);

  // Average across all periods (for the dotted reference line)
  const avgLine = useMemo(() => {
    const vals = chartData
      .flatMap(d => chartLines.map(l => d[l.key]))
      .filter(v => v != null && v > 0);
    return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
  }, [chartData, chartLines]);

  // ── Heatmap rows (all players) ────────────────────────────────────────────────
  const heatmapRows = useMemo(() => allPlayers.map(name => {
    const rowVals = periodOrder.map(p => {
      const v = byPlayerPeriod[name]?.[p]?.[metricKey];
      return v != null ? Number(v) : null;
    });
    return { name, values: rowVals };
  }), [allPlayers, byPlayerPeriod, metricKey, periodOrder]);

  // ── Summary cards ─────────────────────────────────────────────────────────────
  const summary = useMemo(() => {
    if (!allPlayers.length) return null;

    // 1. Highest intensity period (team avg per period)
    let peakPeriod = '—', peakVal = 0;
    periodOrder.forEach((period, idx) => {
      const vals = allPlayers.map(n => Number(byPlayerPeriod[n]?.[period]?.[metricKey]) || 0).filter(v => v > 0);
      if (vals.length) {
        const avg = vals.reduce((s, v) => s + v, 0) / vals.length;
        if (avg > peakVal) { peakVal = avg; peakPeriod = periodLabels[idx] + ' min'; }
      }
    });

    // 2. Player with most total HMLD (use half-total drills if available, else sum periods)
    let topHmldPlayer = '—', topHmldVal = 0;
    for (const name of allPlayers) {
      const total = halfDrills.filter(d => d.player_name === name).reduce((s, d) => s + (Number(d.hmld) || 0), 0);
      if (total > topHmldVal) { topHmldVal = total; topHmldPlayer = name; }
    }
    if (topHmldVal === 0) {
      // Fallback: sum period rows
      for (const name of allPlayers) {
        const total = periodOrder.reduce((s, p) => s + (Number(byPlayerPeriod[name]?.[p]?.hmld) || 0), 0);
        if (total > topHmldVal) { topHmldVal = total; topHmldPlayer = name; }
      }
    }

    // 3. Per-minute intensity drop-off: first 60 min vs the match's own last 30 min.
    //    Only include players who played through most of the match (matchTotalMinutes-15).
    const fatigueList = [];
    for (const name of allPlayers) {
      const totalPeriods = periodOrder.filter(p => byPlayerPeriod[name]?.[p] != null).length;
      if (totalPeriods * 5 < minMinutesForFatigue) continue;

      const first60 = first60Periods.reduce((s, p) => s + (Number(byPlayerPeriod[name]?.[p]?.[metricKey]) || 0), 0);
      const last30  = last30Periods.reduce((s, p)  => s + (Number(byPlayerPeriod[name]?.[p]?.[metricKey]) || 0), 0);
      if (first60 === 0) continue;

      const perMinFirst60 = first60 / 60;
      const perMinLast30  = last30  / 30;
      const dropPct = ((perMinLast30 - perMinFirst60) / perMinFirst60) * 100;
      fatigueList.push({ name, perMinFirst60, perMinLast30, dropPct });
    }
    // Sort ascending: most fatigued (most negative) first
    fatigueList.sort((a, b) => a.dropPct - b.dropPct);
    const top3Fatigue   = fatigueList.slice(0, 3);
    // Top 3 who improved / dropped least — exclude anyone already in fatigue list
    const fatigueNames  = new Set(top3Fatigue.map(p => p.name));
    const top3Strongest = [...fatigueList].reverse().slice(0, 3).filter(p => !fatigueNames.has(p.name));

    // 4. Average minutes played (number of periods × 5 per player, averaged)
    const avgMins = allPlayers.length
      ? allPlayers.reduce((sum, name) => {
          const count = periodOrder.filter(p => byPlayerPeriod[name]?.[p] != null).length;
          return sum + count * 5;
        }, 0) / allPlayers.length
      : 0;

    return { peakPeriod, peakVal, topHmldPlayer, topHmldVal, top3Strongest, top3Fatigue, avgMins };
  }, [allPlayers, byPlayerPeriod, halfDrills, metricKey, periodOrder, periodLabels, first60Periods, last30Periods, minMinutesForFatigue]);

  // ── Shared styles ─────────────────────────────────────────────────────────────
  const card   = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14 };
  const SEL    = { padding: '6px 26px 6px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.83rem', fontFamily: 'inherit', cursor: 'pointer', outline: 'none', appearance: 'none', WebkitAppearance: 'none' };
  const metricLabel = METRICS.find(m => m.key === metricKey)?.label || metricKey;

  const selectedSession = filteredSessions.find(s => String(s.id) === sessionId);
  const selectedOpp = opponents.find(o => o.id === selectedSession?.opponent_id);
  const sessionLabel = (s) => {
    const opp = opponents.find(o => o.id === s.opponent_id);
    const date = fmtDate(s.session_date?.slice(0, 10));
    return opp ? `${opp.name} · ${date}` : `${s.session_title || 'Match'} · ${date}`;
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading…</div>
  );

  if (!sessions.length) return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>In-Match Analysis</h2>
      <div style={{ ...card, padding: 48, textAlign: 'center' }}>
        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
          No match sessions found. Upload match CSVs in the Upload zone first.
        </p>
      </div>
    </div>
  );
  if (!filteredSessions.length) return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>In-Match Analysis</h2>
      <div style={{ ...card, padding: 48, textAlign: 'center' }}>
        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
          No data for this season. Change the season filter in Settings to view other data.
        </p>
      </div>
    </div>
  );

  const hasData = allPlayers.length > 0 && !drillsLoad;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 40 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>In-Match Analysis</h2>
          <p style={{ fontSize: '0.83rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
            Period-by-period load breakdown
          </p>
        </div>
        {onNavigate && sessionId && (
          <button
            onClick={() => {
              localStorage.setItem('comparison_builder_match_id', sessionId);
              onNavigate('gps', 'comparison-builder');
            }}
            style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #3b82f6', background: 'rgba(59,130,246,0.1)', color: '#93c5fd', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
            Comparison builder
          </button>
        )}
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Match selector */}
        <div style={{ position: 'relative' }}>
          <select value={sessionId} onChange={e => setSessionId(e.target.value)} style={{ ...SEL, minWidth: 220 }}>
            {filteredSessions.map(s => (
              <option key={s.id} value={String(s.id)}>{sessionLabel(s)}</option>
            ))}
          </select>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>

        {/* Player multi-select */}
        <PlayerMultiSelect players={allPlayers} selected={selPlayers} onChange={setSelPlayers} />

        {/* Metric */}
        <div style={{ marginLeft: 'auto', position: 'relative' }}>
          <select value={metricKey} onChange={e => setMetricKey(e.target.value)} style={SEL}>
            {METRICS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>

      {drillsLoad && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
          Loading match data…
        </div>
      )}

      {!drillsLoad && !hasData && (
        <div style={{ ...card, padding: 48, textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            No period drill data found for this match. Make sure match drills were uploaded.
          </p>
        </div>
      )}

      {hasData && (
        <>
          {/* ── Section 1: Line chart ── */}
          <div style={{ ...card, padding: '20px 20px 12px', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <span style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                {metricLabel} per period
                {selectedOpp && <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 8 }}>vs {selectedOpp.name}</span>}
              </span>
              <div style={{ display: 'flex', gap: 14, fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 12, height: 8, borderRadius: 2, background: 'rgba(59,130,246,0.15)', display: 'inline-block' }} />First half
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 12, height: 8, borderRadius: 2, background: 'rgba(139,92,246,0.15)', display: 'inline-block' }} />Second half
                </span>
              </div>
            </div>

            {/* Player color legend (individual mode) */}
            {!isAllMode && chartLines.length <= 10 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginBottom: 12 }}>
                {chartLines.map(l => (
                  <span key={l.key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                    <span style={{ width: 16, height: 3, borderRadius: 2, background: l.color, display: 'inline-block' }} />
                    {l.label}
                  </span>
                ))}
              </div>
            )}

            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                  {/* Half shading — splits whatever periods this match has down the middle */}
                  {periodLabels.length > 0 && (
                    <>
                      <ReferenceArea x1={periodLabels[0]} x2={periodLabels[halfCount - 1]} fill="rgba(59,130,246,0.06)" />
                      <ReferenceArea x1={periodLabels[halfCount]} x2={periodLabels[periodLabels.length - 1]} fill="rgba(139,92,246,0.06)" />
                    </>
                  )}
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="period"
                    tick={{ fill: 'var(--text-muted)', fontSize: 8, fontFamily: 'Inter, sans-serif' }}
                    axisLine={false} tickLine={false} interval={0} />
                  <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 10, fontFamily: 'Inter, sans-serif' }}
                    axisLine={false} tickLine={false}
                    tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v} />
                  <Tooltip content={<PeriodTooltip metricLabel={metricLabel} />} />
                  {avgLine != null && (
                    <ReferenceLine y={avgLine} stroke="#f59e0b" strokeDasharray="5 3" strokeWidth={1.5}
                      label={{ value: `Avg ${Math.round(avgLine).toLocaleString()}`, position: 'insideTopRight', fill: '#f59e0b', fontSize: 9, fontFamily: 'Inter, sans-serif' }} />
                  )}
                  {chartLines.map(l => (
                    <Line
                      key={l.key}
                      dataKey={l.key}
                      stroke={l.color}
                      strokeWidth={l.bold ? 2.5 : 1.5}
                      dot={l.bold ? { r: 3, fill: l.color } : { r: 2, fill: l.color }}
                      activeDot={{ r: 5 }}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ── Section 2: Heatmap ── */}
          <div style={{ ...card, marginBottom: 20, overflow: 'auto' }}>
            <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                Period Heatmap — {metricLabel}
              </span>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                Color scale is relative to each player's own peak
              </span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '0.75rem', minWidth: 150 + periodLabels.length * 40 }}>
                <thead>
                  <tr>
                    <th style={{ padding: '7px 14px', textAlign: 'left', fontWeight: 600, fontSize: '0.66rem', letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', background: 'var(--bg-app)', whiteSpace: 'nowrap', position: 'sticky', left: 0, zIndex: 2, width: 150 }}>Player</th>
                    {periodLabels.map((lbl, i) => (
                      <th key={i} style={{ padding: '7px 6px', textAlign: 'center', fontWeight: 600, fontSize: '0.62rem', letterSpacing: '0.05em', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', background: i < halfCount ? 'rgba(59,130,246,0.05)' : 'rgba(139,92,246,0.05)', whiteSpace: 'nowrap' }}>
                        {lbl}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {heatmapRows.map((row, ri) => (
                    <tr key={row.name} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '6px 14px', fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', background: 'var(--bg-app)', position: 'sticky', left: 0, zIndex: 1, width: 150 }} title={row.name}>
                        {row.name}
                      </td>
                      {row.values.map((val, pi) => (
                        <td key={pi} style={{
                          padding: '6px 4px', textAlign: 'center', fontWeight: 500,
                          background: cellBg(val, row.values),
                          color: val ? cellTextColor(val, row.values) : 'var(--text-dimmed)',
                          fontSize: '0.72rem',
                        }}>
                          {val != null && val > 0 ? Math.round(val).toLocaleString() : '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Section 3: Summary cards ── */}
          {summary && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr 1fr', gap: 12 }}>

              {/* Card 1 — Peak period */}
              <div style={{ ...card, padding: '16px 18px' }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 8, lineHeight: 1.3 }}>Highest intensity period</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#f59e0b', lineHeight: 1 }}>{summary.peakPeriod}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-dimmed)', marginTop: 5 }}>Avg {fmt0(summary.peakVal)} {metricLabel}</div>
              </div>

              {/* Card 2 — Most HMLD */}
              <div style={{ ...card, padding: '16px 18px' }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 8, lineHeight: 1.3 }}>Most HMLD</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#10b981', lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={summary.topHmldPlayer}>
                  {summary.topHmldPlayer.split(' ').slice(-1)[0]}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-dimmed)', marginTop: 5 }}>{fmt0(summary.topHmldVal)} m total HMLD</div>
              </div>

              {/* Card 3 — Fatigue leaderboard */}
              <div style={{ ...card, padding: '16px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.3 }}>
                    Last 30 vs first 60 min intensity ({metricLabel}/min)
                  </span>
                  <span style={{ fontSize: '0.66rem', color: 'var(--text-dimmed)', whiteSpace: 'nowrap', marginLeft: 8 }}>≥{minMinutesForFatigue} min only</span>
                </div>
                {summary.top3Strongest.length === 0 && summary.top3Fatigue.length === 0 ? (
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-dimmed)', fontStyle: 'italic' }}>No players with ≥{minMinutesForFatigue} min played</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {/* Column headers */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 60px 52px', gap: 4, fontSize: '0.6rem', fontWeight: 600, color: 'var(--text-dimmed)', letterSpacing: '0.05em', textTransform: 'uppercase', borderBottom: '1px solid var(--border)', paddingBottom: 4 }}>
                      <span>Player</span>
                      <span style={{ textAlign: 'right' }}>0–60/min</span>
                      <span style={{ textAlign: 'right' }}>60–90/min</span>
                      <span style={{ textAlign: 'right' }}>Δ</span>
                    </div>

                    {/* Top 3 strongest / least drop */}
                    {summary.top3Strongest.length > 0 && (
                      <>
                        <div style={{ fontSize: '0.62rem', fontWeight: 600, color: '#34d399', letterSpacing: '0.06em', textTransform: 'uppercase', marginTop: 2 }}>↑ Strongest finish</div>
                        {summary.top3Strongest.map(({ name, perMinFirst60, perMinLast30, dropPct }) => (
                          <div key={name} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 60px 52px', gap: 4, alignItems: 'center' }}>
                            <span style={{ fontSize: '0.76rem', fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                            <span style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', textAlign: 'right' }}>{perMinFirst60.toFixed(1)}</span>
                            <span style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', textAlign: 'right' }}>{perMinLast30.toFixed(1)}</span>
                            <span style={{ fontSize: '0.76rem', fontWeight: 700, textAlign: 'right', color: dropColor(dropPct) }}>
                              {dropPct > 0 ? '+' : ''}{Math.round(dropPct)}%
                            </span>
                          </div>
                        ))}
                      </>
                    )}

                    {/* Separator */}
                    {summary.top3Strongest.length > 0 && summary.top3Fatigue.length > 0 && (
                      <div style={{ height: 1, background: 'var(--border)', margin: '2px 0' }} />
                    )}

                    {/* Top 3 most fatigued */}
                    {summary.top3Fatigue.length > 0 && (
                      <>
                        <div style={{ fontSize: '0.62rem', fontWeight: 600, color: '#f87171', letterSpacing: '0.06em', textTransform: 'uppercase', marginTop: 2 }}>↓ Most fatigued</div>
                        {summary.top3Fatigue.map(({ name, perMinFirst60, perMinLast30, dropPct }) => (
                          <div key={name} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 60px 52px', gap: 4, alignItems: 'center' }}>
                            <span style={{ fontSize: '0.76rem', fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                            <span style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', textAlign: 'right' }}>{perMinFirst60.toFixed(1)}</span>
                            <span style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', textAlign: 'right' }}>{perMinLast30.toFixed(1)}</span>
                            <span style={{ fontSize: '0.76rem', fontWeight: 700, textAlign: 'right', color: dropColor(dropPct) }}>
                              {dropPct > 0 ? '+' : ''}{Math.round(dropPct)}%
                            </span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Card 4 — Avg minutes */}
              <div style={{ ...card, padding: '16px 18px' }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 8, lineHeight: 1.3 }}>Avg minutes played</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#3b82f6', lineHeight: 1 }}>{Math.round(summary.avgMins)}'</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-dimmed)', marginTop: 5 }}>{allPlayers.length} players tracked</div>
              </div>

            </div>
          )}
        </>
      )}
    </div>
  );
}
